import React, { useCallback } from 'react';

import get from 'lodash/fp/get';
import noop from 'lodash/noop';

import { useClassnames } from '@lumapps/classnames';
import type { SortersType } from '@lumapps/lumx-filter-and-sort/types';
import { DOCUMENT_V1_SORTING } from '@lumapps/medias-document/constants';
import {
    useSiteLibraryNavigation,
    type UseSiteLibraryNavigationParams,
} from '@lumapps/medias-document/hooks/useSiteLibraryNavigation';
import type { DocumentV1 } from '@lumapps/medias-document/types';
import { DocumentBlock } from '@lumapps/medias/components/DocumentBlock';
import { FolderBlockSkeletons } from '@lumapps/medias/components/FolderBlock/FolderBlockSkeletons';
import { PagePickerDialog, type PagePickerDialogProps } from '@lumapps/pickers/components/PagePickerDialog';
import { useTranslate, GLOBAL } from '@lumapps/translations';

import { FILE_PICKER_SORTING_FIELDS } from '../../constants';
import { FILE_PICKER } from '../../keys';
import { FilePickerDialogBreadcrumb } from './FilePickerDialogBreadcrumb';

import './index.scss';

export interface BaseFilePickerDialogProps
    extends Pick<UseSiteLibraryNavigationParams, 'searchTypes'>,
        Pick<PagePickerDialogProps<DocumentV1>, 'itemSelectableSelector' | 'sorts'> {
    /**
     * The initial sort order to apply when the dialog opens.
     */
    initialSort?: UseSiteLibraryNavigationParams['sortOrder'];
    /**
     * Controls if the dialog is open or closed.
     */
    isOpen: boolean;
    /**
     * Title of the picker.
     */
    title?: string;
    /**
     * Callback function executed when the user clicks the cancel button or closes the dialog.
     */
    onCancel: () => void;
}

interface FilePickerMultipleProps extends BaseFilePickerDialogProps {
    isMultipleSelect: true;
    /**
     * Callback function executed when the user confirms the selection.
     * It receives the selected file items or `null` if nothing is selected.
     */
    onConfirm: (items?: DocumentV1[]) => void;
}

interface FilePickerSingleProps extends BaseFilePickerDialogProps {
    isMultipleSelect: false | undefined;
    /**
     * Callback function executed when the user confirms the selection.
     * It receives the selected file item or `null` if nothing is selected.
     */
    onConfirm: (item?: DocumentV1) => void;
}

export type FilePickerDialogProps<IsMultiple extends boolean = false> = IsMultiple extends true
    ? FilePickerMultipleProps
    : FilePickerSingleProps;

const CLASSNAME = 'file-picker-dialog';

/**
 * Renders a picker dialog for selecting a single file from the site's media library.
 *
 * @param {FilePickerDialogProps}
 * @returns FilePickerDialog
 */
export const FilePickerDialog = <IsMultiple extends boolean = false>({
    initialSort,
    isOpen,
    searchTypes,
    sorts = FILE_PICKER_SORTING_FIELDS,
    title,
    isMultipleSelect,
    itemSelectableSelector,
    onCancel,
    onConfirm,
}: FilePickerDialogProps<IsMultiple>) => {
    const { translateKey } = useTranslate();
    const { block } = useClassnames(CLASSNAME);

    const {
        folderPath,
        isError,
        isFetchingNextPage,
        isLoading,
        items,
        fetchNextPage,
        navigateBreadcrumb,
        navigateToFolder,
        onSearch,
        onSort,
        refetch,
        resetToRootFolder,
    } = useSiteLibraryNavigation({ isEnabled: isOpen, searchTypes, sortOrder: initialSort });

    const onCancelHandler = () => {
        onCancel();
        resetToRootFolder();
    };

    /**
     * Handles the sort change event from the `PagePickerDialog`.
     *
     * @param {SortersType} sort
     */
    const onSortHandler = (sort: SortersType) => {
        if (sort.id && Object.values(DOCUMENT_V1_SORTING).includes(sort.id as DOCUMENT_V1_SORTING)) {
            onSort(sort.id as DOCUMENT_V1_SORTING);
        }
    };

    const itemRenderer = useCallback((item: DocumentV1) => <DocumentBlock document={item} />, []);

    const onSelectionConfirm = (items: DocumentV1[]) => {
        if (!items.length) {
            onConfirm(undefined);
        } else if (isMultipleSelect) {
            onConfirm(items);
        } else {
            onConfirm(items[0]);
        }
    };

    const itemNavigableSelector = useCallback((item: DocumentV1) => item.isFolder, []);

    return (
        <PagePickerDialog<DocumentV1>
            afterHeader={<FilePickerDialogBreadcrumb folderPath={folderPath} navigateBreadcrumb={navigateBreadcrumb} />}
            className={block()}
            fetchMore={fetchNextPage}
            fetchOnMount={false}
            fetchPage={noop}
            hasError={isError}
            isListLoading={isLoading}
            isListLoadingMore={isFetchingNextPage}
            isLoading={isLoading}
            isMultipleSelect={isMultipleSelect}
            isOpen={isOpen}
            itemKeySelector={get('id')}
            itemNavigableSelector={itemNavigableSelector}
            itemRenderer={itemRenderer}
            items={items}
            itemSelectableSelector={itemSelectableSelector}
            listSkeleton={FolderBlockSkeletons}
            onNavigate={navigateToFolder}
            onReload={refetch}
            onSearch={onSearch}
            onSelectionCancel={onCancelHandler}
            onSelectionConfirm={onSelectionConfirm}
            onSortChange={onSortHandler}
            resetListOnItemsChange
            searchPlaceholder={translateKey(GLOBAL.SEARCH_BY_NAME)}
            shouldPreventClose
            sorts={sorts}
            title={title || translateKey(FILE_PICKER.SELECT_FILE)}
        />
    );
};
